<template>
  <form
    ref="dropzone"
    class="input-panel upload"
    :style="{
      background: debouncedDragged ? '#d3d3d3' : '',
      zIndex: debouncedDragged ? '9999' : 'inherit',
    }"
  >
    <div class="dropzone-text">
      <div>
        {{ t('fileupload.dragAndDrop.title') }}
        <label
          for="select-files"
          class="upload-button"
          tabindex="0"
          :aria-label="t('fileupload.clickHere.aria')"
          @keydown.enter.space.prevent="fileInput!.click()"
        >
          {{ t('fileupload.clickHere.label') }}.
        </label>

        <input
          id="select-files"
          ref="fileInput"
          name="select-files"
          hidden
          type="file"
          :accept="acceptedFileTypes"
          :disabled="!dropZoneEnabled"
          :multiple="maxFiles > 1"
          @change="handleFileSelect"
        >
      </div>

      <div class="small-text">
        <small>
          {{ t('fileupload.maxFileCountAndSize', { maxFiles, maxSize: getFileSizeLabel(maxFilesize) }) }}
        </small>
        <small v-if="acceptedFiles.length > 0">
          {{ t('fileupload.filetypes.allowedLabel') }}: {{ acceptedFileTypesText }}
        </small>
      </div>
    </div>
  </form>
</template>
<script setup lang="ts">
import { refDebounced, useDropZone } from '@vueuse/core';
import { computed, ref, toRef, useTemplateRef } from 'vue';
import { getFileSizeLabel, mapMimeToFilename } from './helper';
import { useI18n } from '@/composables/useI18n';

const props = defineProps<{
  dropzoneEnabled: boolean;
  maxFiles: number;
  acceptedFiles: string[];
  maxFilesize: number;
  currentFileCount: number;
}>();

const { t } = useI18n();
const acceptedFileTypesText = computed(() => {
  return props.acceptedFiles.map((item: string) => {
    return mapMimeToFilename(item);
  })
    // filter out undefined values
    .filter((item) => item?.filename !== undefined)
    // filter get filenames
    .map((item) => item?.filename)
    // split doc and docx
    .map((item) => item?.split(','))
    // flatten the array
    .flat()
    // join the array
    .join(' ');
});

const acceptedFileTypes = computed(() => {
  const types = props.acceptedFiles.map((item: string) => {
    return mapMimeToFilename(item);
  });
  const concattypes = types.map((item) => item?.filename).join(',').concat(
    types.map((item) => item?.mime).join(','),
  );
  return concattypes;
});

const emit = defineEmits<{
  fileSelect: [fileHandler: File[]]
}>()

const dropZoneEnabled = toRef(props, 'dropzoneEnabled');
const isDragged = ref(false);
const debouncedDragged = refDebounced(isDragged, 50)
window.addEventListener("dragover", () => {
  isDragged.value = true;
});
window.addEventListener("dragleave", () => {
  isDragged.value = false;
});
window.addEventListener("drop", () => {
  isDragged.value = false;
});

const uploadForm = useTemplateRef('dropzone');
const fileInput = useTemplateRef('fileInput');
useDropZone(uploadForm, {onDrop,});





// drap and drop files
async function onDrop(files: File[] | null) {
  if (!files) return;
  emit('fileSelect', files);
}

// select files from file dialog
const handleFileSelect = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (!input.files) return;
  const filesArray = Array.from(input.files);
  if (!filesArray || filesArray.length <= 0) return;
  uploadForm?.value?.reset();
  emit('fileSelect', filesArray);
};
</script>
<style lang="scss" scoped>
.input-panel {
  height: 100%;
  padding: 10px 0;


  &.disabled {
    opacity: 0.5;
  }

}

.validation-message {
  color: #e10000;
  display: block;
  font-size: 12px;
  max-width: 100%;
  margin: 0 auto;
}

.upload {
  margin-top: 0;
  //background: rgba(142, 142, 142, 0.2);
  border-radius: 4px;
  border-color: rgba(142, 142, 142, 1);

  border-style: dashed;
  border-width: 1px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  /**
  TODO: Fix the drag-over state that is now broken because background was changed for use with border
   */

  @supports (background-image: url("data:image/svg+xml")) {
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%23333' stroke-width='1' stroke-dasharray='4%2c6' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
    border-width: 0;
  }

  .upload-button {
    appearance: none;
    -moz-appearance: none;
    -webkit-appearance: none;
    border: none;
    background: none;
    padding: 0;

    color: darkblue;
    font-weight: 500;
    cursor: pointer;
    border-radius: var(--kw-widget-global-borderradius-inner);
  }

  .upload-button:hover {
    text-decoration: underline;
  }

  .upload-button:focus,
  .upload-button:focus-visible {
    @include default-focus;
  }

  form {
    padding: 8px;
    margin: 0 auto;
    font-size: 15px;
  }

  small {
    color: #313131;
  }
}

.dropzone-text {
  // pretty text wrap
  text-wrap: balance;
  font-size: 14px;

}

.small-text {
  >* {
    line-height: 1.2;
    display: block;

  }
}

.upload-info {
  display: flex;
  font-size: 14px;
  gap: 10px;
}
</style>
